import React from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import axios from "axios";
import Cookies from "universal-cookie";
import { useDispatch, useSelector } from "react-redux";
import { reduxUpdateUser } from "./redux/reducers/user";
import { reduxUpdateSettings } from "./redux/reducers/settings";
import { baseURLExecute, consoleURL, webURL } from "./services/API";
import { Workflow } from "./pages/workflow";
import { RootState } from "./redux/store";
import { Chatbot } from "./pages/chatbot";
import { AIAgentStudio } from "./pages/ai-agent-studio";
import socket from './services/socket';

let isRegisterSocket = false;

function readAuthTokenFromUrl(): string | null {
   const params = new URLSearchParams(window.location.search);
   const authToken = params.get('auth_token');
   if (!authToken) return null;

   params.delete('auth_token');
   const search = params.toString();
   const nextUrl = `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`;
   window.history.replaceState({}, '', nextUrl);
   return authToken;
}

export default function App() {
   const userToken = useSelector((state: RootState) => state.user.data?.token);
   const userCredits = useSelector((state: RootState) => state.user.data?.plan);
   const theme = useSelector((state: RootState) => state.settings.data.theme);
   const dispatch = useDispatch();
   const [isCreditsMessage, setIsCreditsMessage] = React.useState(false);

   // Initialize theme
   React.useEffect(() => {
      const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' || 'dark';
      dispatch(reduxUpdateSettings({ theme: savedTheme }));
      document.documentElement.setAttribute('data-theme', savedTheme);
   }, [dispatch]);

   // Save theme to localStorage when it changes
   React.useEffect(() => {
      localStorage.setItem('theme', theme);
      document.documentElement.setAttribute('data-theme', theme);
   }, [theme]);

   // Hydrate auth from query token (iframe embed) or cookie — without a full page reload.
   React.useEffect(() => { 
      const cookies = new Cookies();
      const authFromUrl = readAuthTokenFromUrl();
      const token = authFromUrl || cookies.get('token');

      if (authFromUrl) {
         cookies.set('token', authFromUrl, {
            path: '/',
            expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365),
         });
      }

      if (!token) return;

      baseURLExecute();
      axios
         .get(`${webURL}/auth`, { headers: { 'x-auth-token': token } })
         .then((response) => {
            dispatch(reduxUpdateUser({ ...response.data.user, token }));
         })
         .catch((err) => {
            console.warn('[Auth] Failed to hydrate session:', err?.message || err);
         });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // ---
   React.useEffect(() => {
      if (!userToken) {
         if (socket.connected) socket.disconnect();
         isRegisterSocket = false;
         return;
      }

      socket.auth = { token: userToken };
      const register = () => socket.emit('register', userToken);
      socket.on('connect', register);
      if (!socket.connected) socket.connect();
      else register();
      isRegisterSocket = true;

      return () => {
         socket.off('connect', register);
      };
   }, [userToken]);

   // ---
   React.useEffect(() => {
      if (userToken && userCredits?.subscription === 'Free' && userCredits?.credits.current as number <= 0) {
         setIsCreditsMessage(true);
      }
   }, [userCredits, userToken]);

   return (
      <BrowserRouter>
         { isCreditsMessage ? <div className="creditsMessage">
            <div onClick={ e => e.stopPropagation() }>
               <div className="top">
                  <div>
                     <span><img src="images/alert.png" alt="warning" /></span>
                     <div>
                        <h3>No Credits</h3>
                        <p>Please subscribe to one of our plans to continue using the platform.</p>
                     </div>
                  </div>
               </div>
               <div className="action">
                  <button onClick={ _=> setIsCreditsMessage(false) }>Cancel</button>
                  <Link to="/credits" onClick={ _=> setIsCreditsMessage(false) }>Continue</Link>
               </div>
            </div>
         </div> : null }
         <Routes>
            <Route path="/workflow" element={ <Workflow /> } />
            <Route path="/chatbot" element={ <Chatbot /> } />
            <Route path="/ai-agent-studio" element={ <AIAgentStudio /> } />
            <Route path="*" element={ <ExternalRedirect /> } />
         </Routes>
      </BrowserRouter>
   );
}

function navigateToConsole(path = '') {
   const target = `${consoleURL}${path}`;
   if (window.top && window.top !== window.self) {
      window.top.location.href = target;
      return;
   }
   window.location.href = target;
}

// ---
const ExternalRedirect = () => {
   React.useEffect(() => {
      navigateToConsole();
   }, []);
   return null;
}
